import { supabase, isSupabaseConfigured } from './supabaseClient';

export interface SupplyItem {
  id: string;
  category: string;
  title: string;
  description: string;
  affiliateUrl: string;
  iconName?: string;
  vendorLabel?: string;
}

class SupplyService {
  private suppliesCache: SupplyItem[] | null = null;
  private lastFetchTime: number = 0;
  private fetchPromise: Promise<SupplyItem[]> | null = null;
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  /**
   * Fetch supplies from Supabase
   */
  async fetchSupplies(): Promise<SupplyItem[]> {
    // If there's already a fetch in progress, return that promise
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = (async () => {
      try {
        if (!isSupabaseConfigured()) {
          console.warn('Supabase is not configured, returning empty supply list');
          return this.suppliesCache || [];
        }

        const { data, error } = await supabase
          .from('shop_supplies')
          .select('*')
          .eq('enabled', true)
          .order('display_order', { ascending: true })
          .order('category', { ascending: true });

        if (error) {
          throw error;
        }

        // Transform Supabase data to SupplyItem format
        const supplies: SupplyItem[] = (data || []).map((row) => ({
          id: row.id,
          category: row.category,
          title: row.title,
          description: row.description,
          affiliateUrl: row.affiliate_url,
          iconName: row.icon_name || undefined,
          vendorLabel: row.vendor_label || undefined,
        }));

        this.suppliesCache = supplies;
        this.lastFetchTime = Date.now();

        return supplies;
      } catch (error) {
        console.error('Failed to fetch supplies from Supabase:', error);
        
        // Return cached supplies if available, otherwise empty array
        return this.suppliesCache || [];
      } finally {
        this.fetchPromise = null;
      }
    })();

    return this.fetchPromise;
  }

  /**
   * Get supplies (cached or fetch if needed)
   */
  async getSupplies(forceRefresh: boolean = false): Promise<SupplyItem[]> {
    const now = Date.now();
    const cacheAge = now - this.lastFetchTime;
    const shouldRefresh = forceRefresh || 
      (cacheAge > this.CACHE_DURATION) ||
      this.lastFetchTime === 0;

    if (shouldRefresh) {
      // Fetch in background, don't block
      this.fetchSupplies().catch((error) => {
        console.error('Background supply refresh failed:', error);
      });
    }

    // Return cached supplies immediately if available
    if (this.suppliesCache) {
      return this.suppliesCache;
    }

    // If no cache, wait for fetch
    return this.fetchSupplies();
  }

  /**
   * Get supplies synchronously (cached only)
   */
  getSuppliesSync(): SupplyItem[] {
    return this.suppliesCache || [];
  }

  /**
   * Initialize supplies (call on app start)
   */
  async initialize(): Promise<void> {
    try {
      await this.fetchSupplies();
    } catch (error) {
      console.error('Failed to initialize supplies:', error);
      // Don't throw - use cached/empty array
    }
  }
}

// Export singleton instance
export const supplyService = new SupplyService();

